import React, { useState, useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';

interface ProcessingTimerProps {
  startedAt?: string; // ISO timestamp
  createdAt: string; // ISO timestamp (fallback)
}

const ProcessingTimer: React.FC<ProcessingTimerProps> = ({ startedAt, createdAt }) => {
  const [elapsed, setElapsed] = useState<number>(0);
  // Ref to track local increment to ensure smooth updates regardless of re-renders
  const localSecondsRef = useRef<number>(0);

  useEffect(() => {
    const startTimeStr = startedAt || createdAt;
    // Handle potential invalid date strings gracefully
    const dateObj = new Date(startTimeStr);
    if (isNaN(dateObj.getTime())) {
      return;
    }
    
    const startTime = dateObj.getTime();
    const mountTime = Date.now();
    
    // Calculate initial difference. 
    // If server time is in the "future" (due to clock skew or timezone issues), 
    // default to 0 to ensure the timer starts counting immediately.
    let initialDiff = Math.floor((mountTime - startTime) / 1000);
    if (initialDiff < 0) {
      initialDiff = 0;
    }

    setElapsed(initialDiff);
    localSecondsRef.current = 0;

    const interval = setInterval(() => {
      localSecondsRef.current += 1;
      setElapsed(initialDiff + localSecondsRef.current);
    }, 1000);

    return () => clearInterval(interval);
  }, [startedAt, createdAt]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  };

  return (
    <div className="flex items-center gap-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full animate-in fade-in">
      <Clock className="h-4 w-4" />
      <span>{formatTime(elapsed)}</span>
    </div>
  );
};

export default ProcessingTimer;
